import './button.css'

const ELEMENT_NAME = 'wp-button'

/**
 * TODO: Icon?
 */

/**
 * A custom web component representing a Button with controllable state.
 *
 * Usage:
 * ```html
 * <wp-button value="my-button" data-color="primary" data-size="small" data-style="outline" data-active data-disabled data-loading>My Button</wp-button>
 *
 * const button = document.querySelector('wp-button')
 * ```
 *
 * You can also create it programmatically:
 * ```js
 * const button = new Button({ label: 'My Button', value: 'my-button', color: 'primary', size: 'small', style: 'outline', active: true, disabled: true, loading: true });
 * document.body.appendChild(button);
 * ```
 * And control it by code:
 * ```js
 * button.addEventListener('wp:onClick', function (event) {
 *   console.log('wp:onClick', event)
 * })
 *
 * button.active = true
 * button.active = false
 * ```
 *
 * Methods:
 * ```js
 * button.active = true|false // Setter
 * const active = button.active // Getter
 * const value = button.value // Getter
 *
 * // Other properties, using native JS methods:
 * button.dataset[property] = '' | delete button.dataset[property]
 * button.setAttribute('property', '') | button.removeAttribute('property')
 * ```
 * Events:
 * wp:onClick - Actual click event
 * onActiveChange - Triggered when the active state changes: might be through code or user interaction
 *
 * @param {Object} options - Configuration options for the Button.
 *
 * Properties
 * @param {string} options.label - The label of the button. (Default: "Button Label")
 * @param {string} options.icon - The icon of the button. (Default: "Button Label")
 * @param {string} options.value - The value of the button. (Defaults to the generated id)
 *
 * Styling
 * @param {string} options.color - The color of the button. (Default = "none") primary|secondary|success|warning|danger
 * @param {string} options.size - The size of the button. (Default = "normal"). small|normal|large
 * @param {string} options.style - TODO The style of the button. (Default = "neutral"). none|link|outline|text
 *
 * States: Can be updated
 * @param {Object} options.active - true|false (Default: "false")
 * @param {Object} options.disabled - true|false (Default: "false")
 * @param {Object} options.loading - true|false (Default: "false")
 * @param {Object} options.toggable - true|false (Default: "false")
 *
 */
export default class Button extends HTMLElement {
  static observedAttributes = [
    'data-label', // Observed data
    'data-color', 'data-size', // Observed styles
    'data-active', 'data-disabled', 'data-loading', 'data-toggable' // Observed states
  ] // , 'color', 'size']

  static defaultTag = ELEMENT_NAME

  constructor (options) {
    super()
    // console.log(`constructor for ${ELEMENT_NAME} start`, this)

    this.$componentInfo = {
      // Component specific state
      elementId: `${ELEMENT_NAME}_` + Math.random().toString(36).substring(2, 9),
      initDone: false,
      renderCount: 0,
      clicks: 0
    }

    // Initialize component state
    this.$data = {
      label: options?.label || this.innerText || 'Click Me',
      html: options?.html || this.innerHTML || options?.label || 'Click Me'
      // label: options?.label || 'Click Me'
    }
    this.$data.value = options?.value || this.getAttribute('value') || this.$componentInfo.elementId

    this.$style = {
      color: options?.color || 'neutral', // primary|secondary|success|warning|danger
      size: options?.size || 'normal' // small|normal|large
    }

    this.$state = {
      // All default to false
      active: !!options?.active,
      disabled: !!options?.disabled,
      loading: !!options?.loading,
      toggable: !!options?.toggable
    }

    // console.log(`constructor for ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`, this)
    // console.log('this.innerHTML=', this.innerHTML)
    // console.log('this.innerText=', this.innerText)
    // console.log('this.textContent=', this.textContent)
  }

  connectedCallback () {
    // console.log(`connectedCallback() for ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`)
    // console.log('dataset=', this.dataset)
    // console.log('$state=', this.$state)
    this.render()

    this.$componentInfo.initDone = true
  }

  disconnectedCallback () {
    // Remove listener when Button is removed
    console.log(`Custom element removed from page: ${ELEMENT_NAME} id=${this.$componentInfo.elementId}`)
  }

  attributeChangedCallback (name, oldValue, newValue) {
    // console.log(`${ELEMENT_NAME} ${this.$componentInfo.elementId} > Attribute ${name} has changed from [${oldValue} ${typeof oldValue}] to [${newValue} ${typeof newValue}]`)

    if (oldValue === newValue) return

    let forcedChangeAttribute = false

    const datasetPropertyName = name.replace('data-', '')

    // Data properties
    if (['label'].includes(datasetPropertyName)) {
      this.$data[datasetPropertyName] = newValue
    }

    // State properties: should be '' or null so the attribute is set without value in the HTML DOM
    if (['active', 'disabled', 'loading', 'toggable'].includes(datasetPropertyName)) {
      // We want only '' or undefined for the attribute value
      // console.log('dataset[datasetPropertyName] ', datasetPropertyName, '=', this.dataset[datasetPropertyName])
      if (this.dataset[datasetPropertyName] !== undefined && this.dataset[datasetPropertyName] !== '') {
        this.dataset[datasetPropertyName] = ''
        forcedChangeAttribute = true
      }
      // console.log('set $state[datasetPropertyName]=', datasetPropertyName, (this.dataset[datasetPropertyName] === ''))
      this.$state[datasetPropertyName] = this.dataset[datasetPropertyName] === ''
      // console.log('state attribute changed in attributeChangedCallback', datasetPropertyName, this.$state[datasetPropertyName])

      if (datasetPropertyName === 'loading') {
        this.dataset.loadingStyle = this.dataset.loadingStyle || 'outline'
      }
    }

    // Style properties
    if (['color', 'size'].includes(datasetPropertyName)) {
      this.$style[datasetPropertyName] = newValue
    }

    // As component init, all the attributes are read before connectedCallback() is called
    if (!this.$componentInfo.initDone) return

    if (forcedChangeAttribute) return
    this.render()
  }

  // Shortcut methods for properties setters and getters
  set active (value) {
    // console.log('set active', value, !!value)
    value ? this.dataset.active = '' : delete this.dataset.active
  }

  // Simplification methods (instead of instance.dataset.active)
  get active () {
    return this.$state.active
  }

  get value () {
    // console.log('get value', this.$data.value, this)
    return this.$data.value
  }

  // Rendering the component
  render () {
    // console.log('render', this.$componentInfo.elementId)
    this.setAttribute('data-id', this.$componentInfo.elementId)
    // this.setAttribute('tabindex', '0')

    // Button content: the label
    // TODO: add icons?

    // this.innerText = this.$data.label
    // Encapsulate the button in a button tag so that it can be tabbed through
    // Only change the innerHTML if it's the first render, so that the element is not lost if the button is updated
    if (this.$componentInfo.renderCount === 0) this.innerHTML = `<button>${this.$data.html}</button>`

    // States
    // console.log('in render $state=', this.$state)
    for (const [key, value] of Object.entries(this.$state)) {
      // console.log('render > state', `data-${key}`, this.dataset[key], '$state>', value)
      if (value === true && this.dataset[key] === undefined) {
        // console.log('render setAttribute', `data-${key}`, '')
        this.dataset[key] = ''
      } else if (value === false && this.dataset[key] !== undefined) {
        // console.log('render removeAttribute', `data-${key}`)
        delete this.dataset[key]
      }
    }

    // Styles
    // if (this.$style.color && this.dataset.color !== this.$style.color) {
    //   // console.log('style attribute changed in render', 'data-color', this.$style.color)
    //   this.dataset.color = this.$style.color
    //   // this.classList.remove(
    //   //   ...['primary', 'secondary', 'danger', 'link', 'warning', 'info'].filter(v => v !== this.$style.color))

    //   // this.classList.add(this.$style.color)
    // }

    for (const [key, value] of Object.entries(this.$style)) {
      // console.log('render > style', `data-${key}`, this.dataset[key], '$style>', value)
      if (value && this.dataset[key] !== value && value !== 'neutral') {
        // console.log('render setAttribute', `data-${key}`, value)
        this.dataset[key] = value
      }
    }

    // Add the listeners (Only once per component instance)
    if (!this.$onClickListenerAdded) {
      this.addEventListener('click', this.onClick)
      // this.addEventListener('keydown', (event) => this.onKeyDown(event))
      this.$onClickListenerAdded = true
    }

    this.$componentInfo.renderCount++
  }

  /**
   *
   * @param {Event} event
   */
  onClick (event) {
    event.stopPropagation()
    if (this.$state.disabled === true) return

    this.$componentInfo.clicks++

    if (this.$state.toggable) {
      this.$state.active = this.$state.active !== true
    }

    this.render()

    // console.log('Button clicked!', this.$componentInfo.elementId, this.$componentInfo.clicks, this.$state.active)
    this.dispatchEvent(new CustomEvent('wp:onClick', {
      detail: {
        component: this,
        clicks: this.$componentInfo.clicks,
        active: this.$state.active
      },
      bubbles: true,
      composed: true
    }))
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, Button)
}
